import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import baseURL from '../../Config';
import { createMapLayout } from './RouteCityMap';

export function positionStatus(row, now) {
    if (!row.active) return 'Detenido';
    if (new Date(row.expiresAt).getTime() <= now) return 'Prueba finalizada';
    if (!row.capturedAt) return 'Esperando ubicación';
    return now - new Date(row.capturedAt).getTime() > 60000 ? 'Sin actualización reciente' : 'Ubicación reciente';
}

const gpsError = (error) => ({
    1: 'Permiso de ubicación denegado. Puedes habilitarlo en los permisos de este sitio e iniciar otra vez.',
    2: 'No se pudo obtener tu ubicación. Comprueba que la ubicación del teléfono esté activada.',
    3: 'El GPS tardó demasiado. Se intentará de nuevo mientras esta pantalla esté visible.'
}[error.code] || 'No se pudo obtener la ubicación.');

export default function LiveLocationPilot({ token }) {
    const [rows, setRows] = useState([]);
    const [sharing, setSharing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [readError, setReadError] = useState('');
    const [now, setNow] = useState(Date.now());
    const [lastSent, setLastSent] = useState(null);
    const session = useRef(null);
    const generation = useRef(0);
    const mounted = useRef(true);
    const capturing = useRef(false);
    const clockOffset = useRef(0);
    const lastTimestamp = useRef(0);
    const pendingStop = useRef(null);

    const request = useCallback((method, suffix, data) => axios({
        method, url: baseURL + 'parlevel/live-locations' + suffix, data, timeout: 15000,
        headers: { authorization: 'Bearer ' + token }
    }), [token]);

    const refresh = useCallback(async () => {
        try {
            const response = await request('get', '');
            if (!mounted.current) return;
            if (response.data.serverTime) clockOffset.current = new Date(response.data.serverTime).getTime() - Date.now();
            setRows(Array.isArray(response.data.data) ? response.data.data : []);
            setReadError('');
        } catch {
            if (mounted.current) setReadError('No se pudo actualizar el mapa. Las posiciones mostradas pueden estar desactualizadas.');
        }
    }, [request]);

    const stop = useCallback(async () => {
        generation.current += 1;
        const previous = session.current || pendingStop.current;
        session.current = null;
        capturing.current = false;
        setSharing(false);
        setBusy(true);
        setMessage('El envío desde este teléfono está detenido.');
        setError('');
        try {
            if (previous) await request('post', '/stop', { sessionId: previous.sessionId });
            pendingStop.current = null;
            await refresh();
        } catch {
            pendingStop.current = previous;
            if (mounted.current) setError('El teléfono dejó de enviar, pero falta confirmar la detención en el servidor. Pulsa «Confirmar detención» al recuperar conexión.');
        } finally {
            if (mounted.current) setBusy(false);
        }
    }, [request, refresh]);

    const capture = useCallback(() => {
        const current = session.current;
        if (!current || capturing.current || document.visibilityState === 'hidden') return;
        if (Date.now() + clockOffset.current >= new Date(current.expiresAt).getTime()) {
            stop();
            return;
        }
        const attempt = generation.current;
        capturing.current = true;
        navigator.geolocation.getCurrentPosition(async (position) => {
            if (!mounted.current || attempt !== generation.current) return;
            try {
                if (position.timestamp <= lastTimestamp.current) return;
                await request('post', '/position', {
                    sessionId: current.sessionId,
                    latitude: position.coords.latitude, longitude: position.coords.longitude,
                    accuracyMeters: position.coords.accuracy, capturedAt: position.timestamp
                });
                if (!mounted.current || attempt !== generation.current) return;
                lastTimestamp.current = position.timestamp;
                setLastSent(new Date());
                setError('');
                setMessage('Compartiendo ubicación mientras Syncro esté visible.');
                refresh();
            } catch (failure) {
                if (!mounted.current || attempt !== generation.current) return;
                if ([401, 403, 409].includes(failure.response?.status)) {
                    session.current = null;
                    generation.current += 1;
                    setSharing(false);
                    setMessage('El envío está detenido.');
                }
                setError(failure.response?.data?.message || 'No se pudo enviar la ubicación. Se intentará otra vez; revisa tu conexión.');
            } finally {
                if (attempt === generation.current || !session.current) capturing.current = false;
            }
        }, (failure) => {
            if (!mounted.current || attempt !== generation.current) return;
            capturing.current = false;
            setError(gpsError(failure));
            if (failure.code === 1) {
                session.current = null;
                generation.current += 1;
                setSharing(false);
                setMessage('El envío está detenido.');
                pendingStop.current = current;
                request('post', '/stop', { sessionId: current.sessionId }).then(() => {
                    if (pendingStop.current === current) pendingStop.current = null;
                    refresh();
                }).catch(() => {});
            }
        }, { enableHighAccuracy: true, maximumAge: 0, timeout: 12000 });
    }, [request, refresh, stop]);

    useEffect(() => {
        mounted.current = true;
        refresh();
        const poll = setInterval(refresh, 10000);
        const tick = setInterval(() => setNow(Date.now() + clockOffset.current), 1000);
        return () => {
            mounted.current = false;
            generation.current += 1;
            const previous = session.current;
            session.current = null;
            clearInterval(poll);
            clearInterval(tick);
            if (previous) request('post', '/stop', { sessionId: previous.sessionId }).catch(() => {});
        };
    }, [refresh, request]);

    useEffect(() => {
        if (!sharing) return undefined;
        capture();
        const timer = setInterval(capture, 15000);
        document.addEventListener('visibilitychange', capture);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', capture);
        };
    }, [sharing, capture]);

    const start = async () => {
        if (!window.isSecureContext || !navigator.geolocation) {
            setError('Abre Syncro con HTTPS en un navegador que permita compartir ubicación.');
            return;
        }
        setBusy(true);
        setError('');
        setLastSent(null);
        setMessage('');
        const attempt = ++generation.current;
        try {
            const response = await request('post', '/start');
            if (!mounted.current || attempt !== generation.current) {
                request('post', '/stop', { sessionId: response.data.data.sessionId }).catch(() => {});
                return;
            }
            session.current = response.data.data;
            lastTimestamp.current = 0;
            capturing.current = false;
            setSharing(true);
            setMessage('Esperando permiso y primera lectura del GPS…');
        } catch {
            if (mounted.current) setError('No se pudo iniciar la prueba. Revisa tu conexión y tu sesión de Syncro.');
        } finally {
            if (mounted.current) setBusy(false);
        }
    };

    const layout = useMemo(() => createMapLayout(rows.filter((row) =>
        Number.isFinite(row.latitude) && Number.isFinite(row.longitude)
    ).map((row) => ({ ...row, key: row.userId, lat: row.latitude, lng: row.longitude }))), [rows]);

    return <Card title="Prueba de ubicación en vivo">
        <p className="mt-0">Desde tu celular, inicia la prueba y permite la ubicación. Desde la computadora, abre esta misma pantalla para observarla sin iniciar otra prueba.</p>
        <p>Se comparte solo al iniciar, durante un máximo de 2 horas. La última posición queda visible hasta 24 horas para administración y soporte. Al bloquear el teléfono o cambiar de aplicación, las actualizaciones pueden detenerse. Al salir de esta pantalla se detiene el envío; al volver debes iniciar otra prueba.</p>
        <div className="flex flex-wrap gap-2">
            <Button label={sharing ? 'Detener mi ubicación' : 'Compartir mi ubicación'} icon={sharing ? 'pi pi-stop' : 'pi pi-map-marker'} onClick={sharing ? stop : start} disabled={busy || Boolean(pendingStop.current)} />
            {pendingStop.current && <Button label="Confirmar detención" onClick={stop} disabled={busy} />}
            <Button label="Actualizar ubicaciones" className="p-button-outlined" onClick={refresh} />
        </div>
        <p role="status">{message}{lastSent ? ' Último envío confirmado: ' + lastSent.toLocaleTimeString() + '.' : ''}</p>
        {error && <p role="alert" className="text-red-600">{error}</p>}
        {readError && <p role="alert" className="text-red-600">{readError}</p>}
        {layout && <>
            <svg viewBox="0 0 1100 500" role="img" aria-label="Mapa de últimas ubicaciones compartidas" style={{ width: '100%', background: '#e2e8f0', borderRadius: 8 }}>
                {layout.tiles.map((tile) => <image key={tile.key} href={tile.href} x={tile.x} y={tile.y} width="256" height="256" />)}
                {layout.points.map((point, index) => <g key={point.key}>
                    <title>{point.name + ': ' + positionStatus(point, now)}</title>
                    <circle cx={point.x} cy={point.y} r="18" stroke="white" strokeWidth="3" fill={positionStatus(point, now) === 'Ubicación reciente' ? '#15803d' : '#64748b'} />
                    <text x={point.x} y={point.y + 5} textAnchor="middle" fill="white" fontSize="15">{index + 1}</text>
                </g>)}
            </svg>
            <small>© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</a>. Verde: lectura reciente. Gris: detenida o sin actualización reciente.</small>
        </>}
        {!rows.length && <p>Nadie ha compartido ubicación en las últimas 24 horas.</p>}
        <div className="flex flex-column gap-2 mt-3">
            {rows.map((row) => {
                const index = layout?.points.findIndex((point) => point.key === row.userId) ?? -1;
                return <div key={row.userId} className="p-3 surface-100 border-round">
                    <b>{index >= 0 ? (index + 1) + '. ' : ''}{row.name}</b> · {positionStatus(row, now)}
                    {row.capturedAt && <div>Última lectura: {new Date(row.capturedAt).toLocaleTimeString()} · Hace {Math.max(0, Math.floor((now - new Date(row.capturedAt).getTime()) / 1000))} s · Precisión aproximada: {Math.round(row.accuracyMeters)} m</div>}
                </div>;
            })}
        </div>
    </Card>;
}
